// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { moveActive } from '../../bygning/palette';
import styles from './CommandPalette.module.css';

export interface PaletteItem {
  id: string;
  label: string;
  /** The right-hand column (group or code). */
  hint: string;
}

export interface CommandPaletteProps {
  open: boolean;
  /** The items for a query, best first (e.g. `filterCommands`). */
  search: (query: string) => readonly PaletteItem[];
  onRun: (id: string) => void;
  onClose: () => void;
}

/**
 * The ⌘K palette: a modal search box over a list. ↑/↓ move, Enter runs,
 * Esc or a click outside closes; focus returns to where it was. The input is
 * a combobox over a listbox, so a screen reader hears the active row.
 */
export function CommandPalette({ open, search, onRun, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const listId = useId();
  const items = useMemo(() => (open ? search(query) : []), [open, search, query]);

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setQuery('');
    setActive(0);
    inputRef.current?.focus();
    return () => {
      // Hand focus back unless something else already took it.
      if (returnTo.current?.isConnected && (document.activeElement === document.body || document.activeElement === null)) {
        returnTo.current.focus();
      }
    };
  }, [open]);

  const at = Math.min(active, Math.max(0, items.length - 1));
  useEffect(() => {
    if (!open) return;
    document.getElementById(`${listId}-${at}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, at, listId]);

  if (!open) return null;

  function run(index: number) {
    const item = items[index];
    if (!item) return;
    onClose();
    onRun(item.id);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(moveActive(at, e.key === 'ArrowDown' ? 1 : -1, items.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(at);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === 'Tab') {
      // The input is the dialog's only stop; keep focus inside.
      e.preventDefault();
    }
  }

  return (
    <div
      className={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.box} role="dialog" aria-modal="true" aria-label="Kommandopalet">
        <input
          ref={inputRef}
          className={styles.input}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Søg type, rum, handling…"
          aria-label="Søg type, rum, handling"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={items.length > 0 ? `${listId}-${at}` : undefined}
          autoComplete="off"
          spellCheck={false}
        />
        <div className={styles.list} id={listId} role="listbox" aria-label="Resultater">
          {items.length === 0 ? (
            <p className={styles.empty}>Intet fundet</p>
          ) : (
            items.map((item, i) => (
              <div
                key={item.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === at}
                className={i === at ? `${styles.option} ${styles.on}` : styles.option}
                onMouseMove={() => i !== at && setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => run(i)}
              >
                <span className={styles.label}>{item.label}</span>
                <span className={styles.hint}>{item.hint}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
